import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualAdvisorConfig } from './VirtualAdvisorProvider';
import { CONSTANTS } from '../components/Helpers/Configuration';

// Inject styles directly to ensure they work in Experience extension builds
const VA_STYLES = `
.va-sr-only { position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important; }
.va-overlay { pointer-events:none; position:fixed; visibility:hidden; opacity:0; width:100%; height:100%; inset:0; background-color:rgba(0,0,0,0.3); transition:all 250ms ease; }
.va-overlay.va-visible { visibility:visible; opacity:1; }
.va-window { position:fixed; background-color:#f1f1f1; border:none; box-shadow:5px 5px 10px rgba(128,128,128,.2); text-align:center; right:-100%; top:0; bottom:0; width:600px; padding-bottom:30px; visibility:hidden; opacity:0; transition:all 250ms ease; }
.va-window.va-window-open { right:0; visibility:visible; opacity:1; }
.va-window-header { display:flex; justify-content:flex-end; color:#fff; padding:8px 10px 13px 0; height:10px; }
.va-header-btn { height:24px; width:24px; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; margin-top:-6px; font-size:24px; padding-left:5px; color:#fff; background:transparent; border:none; }
.va-header-btn:focus { outline:2px solid #FFF; outline-offset:2px; }
#va-iframe { border:none; padding:0 0 20px 0; width:100%; height:100%; margin:0; }
.va-open-button { position:fixed; right:30px; bottom:30px; border-radius:50%; font-size:16px; font-family:Arial, Helvetica, sans-serif; text-align:center; white-space:nowrap; width:56px; height:56px; display:flex; align-items:center; justify-content:center; cursor:pointer; z-index:10; border:2px solid #111; box-shadow:0 8px 20px rgba(0,0,0,.22); }
.va-open-button:focus { outline:none; box-shadow:0 0 0 3px rgba(17,17,17,.28), 0 8px 20px rgba(0,0,0,.22); }
.va-open-button img { display:block; filter:drop-shadow(0 0 1px rgba(255,255,255,.65)); }
@media (max-width: 768px) { .va-window { left:0!important; margin-right:0!important; min-width:100%; width:100%; } }
`;

// Inject styles once into the document
const injectStyles = (() => {
    let injected = false;

    return () => {
        if (injected || typeof document === 'undefined') {
            return;
        }

        const style = document.createElement('style');
        style.id = 'va-widget-styles';
        style.textContent = VA_STYLES;
        document.head.appendChild(style);
        injected = true;
    };
})();

const IconClose = (props) => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" {...props}>
        <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 0 0-1.41 1.42L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/>
    </svg>
);

const IconMinus = (props) => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" {...props}>
        <path fill="currentColor" d="M5 12.75h14v-1.5H5z"/>
    </svg>
);

export function VirtualAdvisorWidget({
    launcherColor = '#320070',
    launcherIconUrl,
    onOpen,
    onClose,
    currentUser,
    currentRoute,
}) {
    const { enabled, showForUser, iframeSrc, origin, zIndex } = useVirtualAdvisorConfig();
    const canShow = enabled && showForUser?.(currentUser, currentRoute);

    useEffect(() => {
        injectStyles();
    }, []);

    const [open, setOpen] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);

    const iframeRef = useRef(null);
    const openBtnRef = useRef(null);
    const nonModalNodes = useRef([]);

    const [liveMsg, setLiveMsg] = useState('');
    const resolvedLauncherIconUrl = launcherIconUrl || CONSTANTS.virtualAdvisor?.defaultIcon;
    const safeIframeSrc = useMemo(
        () => (iframeLoaded ? iframeSrc : ''),
        [iframeLoaded, iframeSrc]
    );

    const handleOpen = useCallback(() => {
        if (!canShow) {
            return;
        }

        setOpen(true);
        setLiveMsg('You have opened the Virtual Advisor Chat');
        if (!iframeLoaded) {
            setIframeLoaded(true);
        }

        const modal = document.getElementById('va-window');
        const modalNodes = Array.from(modal?.querySelectorAll('*') || []);
        nonModalNodes.current = Array.from(document.querySelectorAll('body *:not(#va-window):not([tabindex="-1"])'));

        for (const node of nonModalNodes.current) {
            if (!modalNodes.includes(node)) {
                node._prevTabindex = node.getAttribute('tabindex');
                node.setAttribute('tabindex', -1);
                node.style.outline = 'none';
            }
        }

        onOpen?.();
    }, [canShow, iframeLoaded, onOpen]);

    const restoreTabIndex = useCallback(() => {
        for (const node of nonModalNodes.current) {
            if (!node) {
                continue;
            }

            if (node._prevTabindex !== undefined && node._prevTabindex !== null) {
                node.setAttribute('tabindex', node._prevTabindex);
                node._prevTabindex = null;
            }
            else {
                node.removeAttribute('tabindex');
            }

            node.style.outline = null;
        }

        nonModalNodes.current = [];
    }, []);

    const closeWindow = useCallback((leavingChat = false, isMinimized = false) => {
        if (leavingChat) {
            setIframeLoaded(false);
            if (iframeRef.current) {
                iframeRef.current.src = '';
            }
            setLiveMsg('You have closed the Virtual Advisor Chat. The chat has ended. If you have further questions, please start a new chat session.');
        }
        else if (isMinimized) {
            setLiveMsg('You have minimized the Virtual Advisor Chat');
        }
        else {
            setLiveMsg('You have closed the Virtual Advisor Chat');
        }

        setOpen(false);
        restoreTabIndex();
        setTimeout(() => openBtnRef.current?.focus(), 0);
        onClose?.();
    }, [onClose, restoreTabIndex]);

    const handleMinimize = useCallback(() => {
        closeWindow(false, true);
    }, [closeWindow]);

    const handleConfirmExit = useCallback(() => {
        try {
            const frame = iframeRef.current;

            if (frame && origin) {
                frame.contentWindow?.postMessage('exitChatConfirmation', origin);
            }
            else {
                closeWindow();
            }
        }
        catch {
            closeWindow();
        }
    }, [origin, closeWindow]);

    useEffect(() => {
        const onMessage = (event) => {
            if (!origin || event.origin !== origin) {
                return;
            }

            if (event.data === 'exitChatConfirmation') {
                closeWindow(true);
            }
            else if (event.data === 'minimizeChatConversation') {
                closeWindow(false, true);
            }
        };

        window.addEventListener('message', onMessage);

        return () => window.removeEventListener('message', onMessage);
    }, [origin, closeWindow]);

    useEffect(() => {
        const onKeyUp = (event) => {
            if (!open) {
                return;
            }

            if (event.key === 'Escape') {
                closeWindow(false, true);
            }
        };

        window.addEventListener('keyup', onKeyUp);

        return () => window.removeEventListener('keyup', onKeyUp);
    }, [open, closeWindow]);

    useEffect(() => {
        const onMouseUp = (event) => {
            if (!open) {
                return;
            }

            const windowElement = document.getElementById('va-window');
            if (windowElement && !windowElement.contains(event.target)) {
                closeWindow(false, true);
            }
        };

        document.addEventListener('mouseup', onMouseUp);

        return () => document.removeEventListener('mouseup', onMouseUp);
    }, [open, closeWindow]);

    if (!canShow) {
        return null;
    }

    return (
        <>
            <div role="region" aria-live="polite" aria-atomic="true" className="va-sr-only">
                <p>{liveMsg}</p>
            </div>

            <div id="va-overlay" style={{ zIndex: zIndex - 1 }} className={`va-overlay ${open ? 'va-visible' : ''}`} />

            <button
                id="va-open-button"
                ref={openBtnRef}
                className="va-open-button"
                style={{ backgroundColor: launcherColor, zIndex }}
                onClick={handleOpen}
                aria-label="Open Virtual Advisor"
            >
                {resolvedLauncherIconUrl && <img src={resolvedLauncherIconUrl} alt="Virtual Advisor" width={32} height={32} onError={(event) => { event.target.style.display = 'none'; }} />}
            </button>

            <section
                id="va-window"
                className={`va-window ${open ? 'va-window-open' : ''}`}
                style={{ zIndex }}
                aria-label="Virtual Advisor Chat"
                aria-modal={open ? 'true' : 'false'}
                role="dialog"
            >
                <header className="va-window-header" style={{ backgroundColor: launcherColor }}>
                    <button className="va-header-btn" aria-label="Minimize" onClick={handleMinimize}>
                        <IconMinus />
                    </button>
                    <button className="va-header-btn" aria-label="Close" onClick={handleConfirmExit}>
                        <IconClose />
                    </button>
                </header>

                <iframe
                    ref={iframeRef}
                    id="va-iframe"
                    title="Virtual Advisor"
                    src={iframeLoaded ? safeIframeSrc : ''}
                    allow="fullscreen"
                />
            </section>
        </>
    );
}
